import { type ContentType, type Post, type ApiResponse, type OmitFirstArg, type Comment, BaseFetchParams } from '../types';
import ky, { KyInstance, Options } from 'ky';

export type MarketPostsParams = BaseFetchParams & {
  marketAddress: string;
  page?: number;
  pageSize?: number;
  lastId?: string;
};

export type MarketPostsApiResult = {
  total: number;
  posts: Post[];
};

/**
 * Get posts for a given market
 * (not auth)
 * @param api The KyInstance
 * @param searchParams - The search parameters
 * @param options
 * @returns The posts for the given market
 * @example
 * const posts = await getMarketPosts({
 *   marketAddress: '1',
 *   page: 1,
 *   pageSize: 10,
 * });
 */
export const getMarketPosts = async (api: KyInstance = ky, searchParams: MarketPostsParams, options?: Options) => {
  return await api.get<ApiResponse<MarketPostsApiResult>>('get-market-posts', { ...options, searchParams }).json();
};

export type PublishPostParams = BaseFetchParams;
export type PublishPostBody = {
  marketAddress: string;
  title: string;
  content: string;
};
type PublishPostApiResult = {
  postUuid: string;
};

/**
 * publish a post
 * (need auth)
 * @param api The KyInstance
 * @param body - The post body
 * @param options
 * @returns The post uuid
 * @example
 * const postUuid = await publishPost({
 *   marketAddress: '1',
 *   title: 'hello',
 *   content: 'world',
 * });
 */
export const publishPost = async (
  api: KyInstance = ky,
  searchParams: PublishPostParams,
  json: PublishPostBody,
  options?: Options
) => {
  return await api.post<ApiResponse<PublishPostApiResult>>('publish-post', { ...options, searchParams, json });
};

export type CommentsParams = BaseFetchParams & {
  postUuid: string;
  rootUuid?: string;
  lastId?: string;
  replyPageSize?: number;
  page?: number;
  pageSize?: number;
};

export type CommentsApiResult = {
  total: string;
  comments: Comment[];
};

/**
 * Get comments for a given post
 * (not auth)
 * @param api The KyInstance
 * @param searchParams - The search parameters
 * @param options
 * @returns The comments for the given post
 * @example
 * const comments = await getComments({
 *   postUuid: '1',
 *   page: 1,
 *   pageSize: 10,
 * });
 */
export const getComments = async (api: KyInstance = ky, searchParams: CommentsParams, options?: Options) => {
  return await api.get<ApiResponse<CommentsApiResult>>('get-comments', { ...options, searchParams }).json();
};

export type PublishCommentParams = BaseFetchParams;

export type PublishCommentBody = {
  postUuid?: string;
  rootUuid?: string;
  parentUuid?: string;
  content: string;
};

type PublishCommentApiResult = {
  commentUuid: string;
};

/**
 * publish a comment
 * (need auth)
 * @param api The KyInstance
 * @param body - The comment body
 * @param options
 * @returns The comment uuid
 * @example
 * const commentUuid = await publishComment({
 *   postUuid: '1',
 *   content: 'hello',
 * });
 */
export const publishComment = async (
  api: KyInstance = ky,
  searchParams: PublishCommentParams,
  json: PublishCommentBody,
  options?: Options
) => {
  return await api.post<ApiResponse<PublishCommentApiResult>>('publish-comment', { ...options, searchParams, json });
};

export type LikeContentParams = BaseFetchParams;
export type LikeContentBody = {
  contentUuid: string;
  contentType: ContentType;
};

/**
 * like a content
 * (need auth)
 * @param api The KyInstance
 * @param body - The content body
 * @param options
 * @returns The response
 * @example
 * const response = await likeContent({
 *   contentUuid: '1',
 *   contentType: 1,
 * });
 */
export const likeContent = async (
  api: KyInstance = ky,
  searchParams: LikeContentParams,
  json: LikeContentBody,
  options?: Options
) => {
  return await api.post<ApiResponse>('like-content', { ...options, searchParams, json });
};

export type UnlikeContentParams = BaseFetchParams;
export type UnlikeContentBody = {
  contentUuid: string;
  contentType: ContentType;
};

/**
 * unlike a content
 * (need auth)
 * @param api The KyInstance
 * @param body - The content body
 * @param options
 * @returns The response
 * @example
 * const response = await unlikeContent({
 *   contentUuid: '1',
 *   contentType: 1,
 * });
 */
export const unlikeContent = async (
  api: KyInstance = ky,
  searchParams: UnlikeContentParams,
  json: UnlikeContentBody,
  options?: Options
) => {
  return await api.post<ApiResponse>('unlike-content', { ...options, searchParams, json }).json();
};

export type CommunityApi = {
  /**
   * Get comments for a given post
   * (not auth)
   * @param api The KyInstance
   * @param searchParams - The search parameters
   * @param options
   * @returns The comments for the given post
   * @example
   * const comments = await getComments({
   *   postUuid: '1',
   *   page: 1,
   *   pageSize: 10,
   * });
   */
  getComments: OmitFirstArg<typeof getComments>;
  /**
   * Get posts for a given market
   * (not auth)
   * @param api The KyInstance
   * @param searchParams - The search parameters
   * @param options
   * @returns The posts for the given market
   * @example
   * const posts = await getMarketPosts({
   *   marketAddress: '1',
   *   page: 1,
   *   pageSize: 10,
   * });
   */
  getMarketPosts: OmitFirstArg<typeof getMarketPosts>;
  /**
   * publish a comment
   * (need auth)
   * @param api The KyInstance
   * @param body - The comment body
   * @param options
   * @returns The comment uuid
   * @example
   * const commentUuid = await publishComment({
   *   postUuid: '1',
   *   content: 'hello',
   * });
   */
  publishComment: OmitFirstArg<typeof publishComment>;
  /**
   * publish a post
   * (need auth)
   * @param api The KyInstance
   * @param body - The post body
   * @param options
   * @returns The post uuid
   * @example
   * const postUuid = await publishPost({
   *   marketAddress: '1',
   *   title: 'hello',
   *   content: 'world',
   * });
   */
  publishPost: OmitFirstArg<typeof publishPost>;
  /**
   * like a content
   * (need auth)
   * @param api The KyInstance
   * @param body - The content body
   * @param options
   * @returns The response
   * @example
   * const response = await likeContent({
   *   contentUuid: '1',
   *   contentType: 1,
   * });
   */
  likeContent: OmitFirstArg<typeof likeContent>;
  /**
   * unlike a content
   * (need auth)
   * @param api The KyInstance
   * @param body - The content body
   * @param options
   * @returns The response
   * @example
   * const response = await unlikeContent({
   *   contentUuid: '1',
   *   contentType: 1,
   * });
   */
  unlikeContent: OmitFirstArg<typeof unlikeContent>;
};

export const createCommunityApi = (initOptions: Options): CommunityApi => {
  const margeOptions = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/community` } as Options;
  const communityApi = ky.create(margeOptions);
  return {
    getComments: (searchParams: CommentsParams, options = margeOptions) => getComments(communityApi, searchParams, options),
    getMarketPosts: getMarketPosts.bind(null, communityApi),
    publishComment: publishComment.bind(null, communityApi),
    publishPost: publishPost.bind(null, communityApi),
    likeContent: likeContent.bind(null, communityApi),
    unlikeContent: unlikeContent.bind(null, communityApi),
  };
};

export default createCommunityApi;
